import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import config from '../config';
import TableSelector from './TableSelector';
import { isTodayLocal, formatTimeGR, nowLocalIso5 } from './shared/dateUtils';
import './Service.css';
import { useIsMobile } from '../Responsive';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);
/**
 * WalkedInSitting
 * ----------------
 * Renders the two toolbar buttons (Walked In, Sitting) and the corresponding modals.
 * Contains its own state and API calls so it can be dropped into different pages.
 *
 * Props:
 *  - areasList: Array of areas (used to build visible area filters)
 *  - onRefresh: () => void   // call to ask parent to refresh tables/states after changes
 */

// define Portal once at module scope
const Portal = ({ children }) =>
  (typeof document !== 'undefined' ? createPortal(children, document.body) : null);

// Small helper: one-line horizontal scroll with arrow hints (mobile only)
function ScrollRow({ isMobile, resetKey, className = '', style, children, scrollAmount = 160, ariaLabel = 'Scroll' }) {
  const rowRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setCanLeft(left);
    setCanRight(right);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const el = rowRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    requestAnimationFrame(update);

    const onResize = () => update();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile, resetKey, update]);

  if (!isMobile) {
    return <div className={className} style={style}>{children}</div>;
  }

  return (
    <div className={`ts-scroll-wrap ${canLeft ? 'can-left' : ''} ${canRight ? 'can-right' : ''}`}>
      <button
        type="button"
        className="ts-scroll-arrow left"
        aria-label={`${ariaLabel} left`}
        onClick={() => rowRef.current?.scrollBy({ left: -scrollAmount, behavior: 'smooth' })}
      >
        ‹
      </button>

      <div ref={rowRef} className={`ts-scroll-viewport ${className}`} onScroll={update} style={style}>
        {children}
      </div>

      <button
        type="button"
        className="ts-scroll-arrow right"
        aria-label={`${ariaLabel} right`}
        onClick={() => rowRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' })}
      >
        ›
      </button>
    </div>
  );
}


// toolbarVariant: 'buttons' | 'icons' | 'auto'
function WalkedInSitting({ areasList = [], onRefresh = () => {}, toolbarVariant = 'buttons' }) {

  const isMobile = useIsMobile();
  // iOS Safari fix: use dynamic viewport height when available
  const VDH = '100dvh';
  const VHH = '100vh'; // fallback
  
  const toolbarMode = toolbarVariant === 'auto' ? (isMobile ? 'icons' : 'buttons') : toolbarVariant;

  const overlayMobileStyle = useMemo(() => (
    isMobile
      ? {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start', // important: top is reachable
          padding: '28px 0',        // gives top/outside area to click and close
          boxSizing: 'border-box',
          overflowY: 'auto',      // ✅ allow vertical scroll when modal content exceeds viewport
          overflowX: 'hidden',    // ✅ prevent horizontal scroll
          maxHeight: `min(${VDH}, ${VHH})`,
        }
      : undefined
  ), [isMobile]);

  const walkedInBoxStyle = useMemo(() => (
    isMobile
      ? {
          width: '96vw',
          maxWidth: '96vw',
          height: 'auto',
          maxHeight: 'calc(100vh - 140px)', 
          padding: '6px 8px',
          overflow: 'hidden',
          borderRadius: 12,
        }
      : null
  ), [isMobile]);
  
  const assignTablesBoxStyle = useMemo(() => (
    isMobile
      ? {
          width: '96vw',
          maxWidth: '96vw',
          height: 'calc(100vh - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          padding: '6px 8px',
          overflow: 'hidden',
          overflowX: 'hidden',
          borderRadius: 12,
          boxSizing: 'border-box',
        }
      : {
        width: 'min(980px, 92vw)',
        maxWidth: '92vw',
        height: 'calc(100vh - 160px)',      // ✅ crucial (not auto)
        maxHeight: 'calc(100vh - 160px)',
        padding: '10px 12px',
        overflow: 'hidden',
        overflowX: 'hidden',
        borderRadius: 12,
        boxSizing: 'border-box',
      }
  ), [isMobile]);

  
  const stickyHeaderStyle = useMemo(() => (
    isMobile
      ? {
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'white',
          paddingBottom: 4,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }
      : null
  ), [isMobile]);


  const iconBtnStyle = useMemo(() => ({
    width: 34,
    height: 34,
    minWidth: 34,
    minHeight: 34,
    padding: 0,
    lineHeight: '34px',
    textAlign: 'center',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.25)',
    background: '#eee',
    boxShadow: '0 0 2px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    fontSize: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textShadow: '0 0 black'
  }), []);


  // ----- shared helpers
  const ALL_FILTERS = useMemo(() => ([
    ['garden','Garden'],
    ['smoking','Smoking'],
    ['outside','Outside'],
    ['inside','Inside'],
    ['view','View'],
    ['fireplace','Fireplace'],
    ['vip','VIP']
  ]), []);

  const visibleFilters = useMemo(() => {
    if (!areasList?.length) return ALL_FILTERS;
    const hasAny = (k) => areasList.some(a => !!a[k]);
    return ALL_FILTERS.filter(([k]) => hasAny(k));
  }, [areasList, ALL_FILTERS]);

  const [areaFilters, setAreaFilters] = useState({
    vip: false, fireplace: false, smoking: false, inside: false, outside: false, view: false, garden:false
  });

  const hasTables = (r) => !!(r?.tables && String(r.tables).trim());

  // ----- Walked-in state
  const [walkedInModal, setWalkedInModal] = useState(false);
  const [numPeople, setNumPeople] = useState(2);
  const [selectedTables, setSelectedTables] = useState([]);

  // ----- Sitting state
  const [sittingModal, setSittingModal] = useState(false);
  const [openReservations, setOpenReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ----- Assign-tables (from Sitting) sub-modal
  const [assignTablesModal, setAssignTablesModal] = useState(false);
  const [assignTablesReservation, setAssignTablesReservation] = useState(null);
  const [assignTablesSelection, setAssignTablesSelection] = useState([]);

  // ----- helpers that depend on state (now safe)
  const activeFilterKeys = useCallback(
    () => Object.entries(areaFilters).filter(([, v]) => v).map(([k]) => k),
    [areaFilters]
  );

  const areaMatchesFilters = useCallback((area) => {
    const keys = activeFilterKeys();
    if (keys.length === 0) return true;
    if (!area) return false;
    return keys.every((k) => !!area[k]);
  }, [activeFilterKeys]);

  const areaFilterFn = useCallback((area) => areaMatchesFilters(area), [areaMatchesFilters]);
  const handleTablesSelected = useCallback((ids) => setSelectedTables(ids), []);
  const onAssignTablesSelected = useCallback((ids) => setAssignTablesSelection(ids), []);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const anyOpen = walkedInModal || sittingModal || (assignTablesModal && !!assignTablesReservation);
    const body = document.body;
    if (anyOpen) body.classList.add('no-scroll');
    else body.classList.remove('no-scroll');
  }, [walkedInModal, sittingModal, assignTablesModal, assignTablesReservation]);

  // Fetch open reservations (today, not completed)
  const fetchOpenReservations = useCallback(async () => {
    try {
      const { data: reservations } = await axios.get(tr('{{API_BASE_URL}}/reservations', {
        API_BASE_URL: config.API_BASE_URL
      }));
      const enriched = await Promise.all(
        reservations.map(async (reservation) => {
          const { data: tables } = await axios.get(
            tr('{{API_BASE_URL}}/assignedtables/reservation/{{id}}', {
              API_BASE_URL: config.API_BASE_URL,
              id: reservation.id
            })
          );
          return { ...reservation, tables: tables.map((t) => t.number).join(', ') };
        })
      );
      setOpenReservations(
        enriched.filter((r) => ((!r.state_id) || (r.state_id < 5)) && isTodayLocal(r.arrival))
      );
    } catch (e) {
      console.error('Error fetching reservations for Sitting', e);
      setOpenReservations([]);
    }
  }, []);

  // Only fetch when the Sitting modal is opened (keeps idle time quiet)
  useEffect(() => {
    if (sittingModal) fetchOpenReservations();
  }, [sittingModal, fetchOpenReservations]);

  // Inline update for people directly in the Sitting list
  const updatePeopleInline = async (res, value) => {
    const people = Math.max(1, Math.min(999, Number(value) || 1));
    // optimistic update
    setOpenReservations((list) => list.map((r) => (r.id === res.id ? { ...r, people } : r)));
    if (selectedReservation?.id === res.id) {
      setSelectedReservation((r) => ({ ...r, people }));
    }
    try {
      await axios.put(tr('{{API_BASE_URL}}/reservations/service/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: res.id
      }), {
        client: res.client ?? null,
        people,
        comment: res.comment ?? null,
        order: res.order ?? 0,
      });
    } catch (e) {
      // revert on error
      setOpenReservations((list) => list.map((r) => (r.id === res.id ? { ...r, people: res.people } : r)));
      if (selectedReservation?.id === res.id) {
        setSelectedReservation((r) => ({ ...r, people: res.people }));
      }
      console.error('people inline update failed', e);
    }
  };
  
  const filteredSittingReservations = useMemo(() => {
    const q = (searchQuery ?? '').toString().toLowerCase().trim();
    if (!q) return openReservations;
    return (openReservations || []).filter((res) => {
      const fn = (res?.client_firstname ?? '').toString().toLowerCase();
      const ln = (res?.client_lastname ?? '').toString().toLowerCase();
      const ph = (res?.client_phone ?? '').toString().toLowerCase();
      return fn.includes(q) || ln.includes(q) || ph.includes(q);
    });
  }, [openReservations, searchQuery]);

  const hasSittingRows = filteredSittingReservations.length > 0;
  
  const sittingBoxStyle = useMemo(() => (
    isMobile
      ? {
          width: '96vw',
          maxWidth: '96vw',
  
          // ✅ Fit content when empty, otherwise allow big list (with internal scroll)
          height: hasSittingRows ? `calc(min(${VDH}, ${VHH}) - 160px)` : 'auto',
          maxHeight: hasSittingRows ? `calc(min(${VDH}, ${VHH}) - 160px)` : `calc(min(${VDH}, ${VHH}) - 240px)`,
  
          padding: '6px 8px',
          overflow: 'hidden',
          overflowX: 'hidden',
          borderRadius: 12,
          boxSizing: 'border-box',
        }
      : {
          width: 'min(920px, 92vw)',
          maxWidth: '92vw',
          height: 'auto',
          maxHeight: 'calc(100vh - 160px)',
          padding: '10px 12px',
          overflow: 'hidden',
          overflowX: 'hidden',
          borderRadius: 12,
          boxSizing: 'border-box',
        }
  ), [isMobile, filteredSittingReservations.length, VDH, VHH]);

  // Freeze the "now" reservation time (LOCAL, 5' grid) at the moment the modal opens
  const walkInTime = useMemo(
    () => (walkedInModal ? nowLocalIso5() : null),
    [walkedInModal]
  );

  // Walked-in: create reservation right away
  const handleCreateWalkedInReservation = async () => {
    try {
      // fall back to "now" on the 5' grid if for any reason walkInTime is null
      await axios.post(tr('{{API_BASE_URL}}/reservations/walkedin', {
        API_BASE_URL: config.API_BASE_URL
      }), {
        people: numPeople,
        tables: selectedTables,
      });
      setSelectedTables([]);
      setWalkedInModal(false);
      onRefresh();
    } catch (error) {
      console.error('Error creating walked-in reservation', error);
    }
  };

  // Sitting: open mini assign-tables modal
  const openAssignTables = async (res) => {
    setAssignTablesReservation(res);
    try {
      const { data } = await axios.get(tr('{{API_BASE_URL}}/assignedtables/reservation/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: res.id
      }));
      const pre = Array.isArray(data) ? data.map(t => Number(t.table ?? t.id)).filter(Boolean) : [];
      setAssignTablesSelection(pre);
    } catch {
      setAssignTablesSelection([]);
    }
    setAssignTablesModal(true);
  };

  // Sitting: confirm Sitting (state=5) for the chosen reservation
  const handleSitting = async () => {
    if (!selectedReservation) return;
    try {
      // If the selected reservation still has no tables but we have a picked selection in the mini-modal,
      // assign them now before setting Sitting.
      if (!hasTables(selectedReservation)
          && assignTablesReservation?.id === selectedReservation.id
          && assignTablesSelection.length > 0) {
        await axios.post(tr('{{API_BASE_URL}}/assignedtables', {
          API_BASE_URL: config.API_BASE_URL
        }), {
          reservationId: selectedReservation.id,
          tables: assignTablesSelection,
        });
      }
      // Confirm sitting
      await axios.post(tr('{{API_BASE_URL}}/assignedstates/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id: selectedReservation.id
      }), { state: 5 });

      // Close & refresh
      setAssignTablesModal(false);
      setAssignTablesReservation(null);
      setAssignTablesSelection([]);
      setSelectedReservation(null);
      setSittingModal(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating reservation state', error);
    }
  };

// ----- UI
return (
  <>
    {isMobile && (
      <style>{`
        @media (max-width: 599px) {
          .ts-mobile-compact .table-selector .tabs-header {
            gap: 6px !important;
            padding: 2px 0 !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .ts-mobile-compact .table-selector .tabs-header button {
            padding: 5px 7px !important;
            font-size: 11px !important;
            border-radius: 8px !important;
            white-space: nowrap;
          }

          .ts-mobile-compact .ts-area-filters-title {
            font-size: 12px !important;
            line-height: 1.1;
          }
          .ts-mobile-compact .ts-area-filters-items {
            margin-top: 4px !important;
            display: flex !important;
            flex-wrap: nowrap !important;   /* one line */
            gap: 8px !important;
            overflow-x: auto !important;    /* scroll if needed */
            -webkit-overflow-scrolling: touch;
          }
          .ts-mobile-compact .ts-area-filters-items label {
            font-size: 12px !important;
            white-space: nowrap;
          }
        }
      `}</style>
    )}
  
    {/* Toolbar buttons (intended to be placed inside a header) */}
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      {toolbarMode === 'icons' ? (
        <>
          <button
            type="button"
            style={iconBtnStyle}
            title={tr('Sitting')}
            aria-label={tr('Sitting')}
            onClick={() => setSittingModal(true)}
          >
            <div style={{position: 'relative', width: 50, height: 50}}>
              <span style={{position: 'absolute', top: 1, left: 2, zIndex: 0, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
              <span style={{position: 'absolute', top: 5, left: 5, zIndex: 1, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
              <span style={{position: 'absolute', top: 9, left: 8, zIndex: 2, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
            </div>
          </button>
          <button
            type="button"
            style={iconBtnStyle}
            title={tr('Walked In')}
            aria-label={tr('Walked In')}
            onClick={() => setWalkedInModal(true)}
          >
            {'\u{1F6B6}'} 
          </button>
        </>
      ) : (
        <>
          <button className="rlToolbarBtn" style={{ display: 'inline-flex', alignItems: 'center' }} onClick={() => setSittingModal(true)}>
            <div style={{position: 'relative', width: 30, height: 24, marginTop: '-4px'}}>
              <span style={{position: 'absolute', top: 1, left: 2, zIndex: 1, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
              <span style={{position: 'absolute', top: 5, left: 5, zIndex: 2, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
              <span style={{position: 'absolute', top: 9, left: 8, zIndex: 3, textShadow: '0 0 black'}}>{'\u{1F3AB}'}</span>
            </div>
            {tr('Sitting')}
          </button>
          <button className="rlToolbarBtn" onClick={() => setWalkedInModal(true)}>
            {'\u{1F6B6}'} {tr('Walked In')}
          </button>
        </>
      )}
    </div>

    {/* Walked-In Modal */}
    {walkedInModal && (
      <Portal>
        <div className="modal-overlay" onClick={() => setWalkedInModal(false)} style={overlayMobileStyle}>
          <div className={`modal-walkedin ${isMobile ? '' : 'centered'}`}
            onClick={(e) => e.stopPropagation()}
            style={walkedInBoxStyle || undefined}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',     // ✅ important for desktop/tablet too
                minHeight: 0,       // ✅ allows inner flex child to scroll
                overflowX: 'hidden' // ✅ prevent horizontal scroll
              }}
            >
              <div style={stickyHeaderStyle || undefined}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <b style={{ marginRight: 4 }}>{tr('Clients Walked In:')}</b>
                  
                  <label style={{ margin: 5 }}>{"\u{1F468}"}</label>
                  
                  {Number(numPeople) > 1 && (
                    <button
                      type="button"
                      className="people-step"
                      onClick={() => setNumPeople((p) => Math.max(1, Number(p || 1) - 1))}
                      title={tr('-1 seat')}
                    >
                      -
                    </button>
                  )}
    
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={numPeople}
                    onChange={(e) => setNumPeople(Math.max(1, Math.min(999, Number(e.target.value) || 1)))}
                    style={{ width: 30, textAlign: 'center', padding: '3px' }}
                  />
    
                  <button
                    type="button"
                    className="people-step"
                    onClick={() => setNumPeople((p) => Math.min(999, Number(p || 1) + 1))}
                    title={tr('+1 seat')}
                  >
                    +
                  </button>
    
                  <button
                    onClick={handleCreateWalkedInReservation}
                    disabled={!selectedTables.length}
                    style={{ marginLeft: 'auto' }}
                  >
                    {tr('Save Reservation')}
                  </button>
                </div>
    
                {/* Area Filters (compact on mobile via CSS) */}
                <div className="ts-area-filters" style={{ marginTop: 6 }}>
                  <h4 className="ts-area-filters-title" style={{ margin: 0 }}>{tr('Area Filters:')}</h4>
                
                  <ScrollRow
                    className="ts-area-filters-items"
                    isMobile={isMobile}
                    resetKey={walkedInModal ? 1 : 0}
                    desktopStyle={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}
                  >
                    {visibleFilters.map(([key, label]) => (
                      <label key={key} style={{ userSelect: 'none', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!areaFilters[key]}
                          onChange={(e) => setAreaFilters(prev => ({ ...prev, [key]: e.target.checked }))}
                          style={{ marginRight: 6, ...(isMobile ? {} : { position: 'relative', top: 5 }) }}
                        />
                        {tr(label)}
                      </label>
                    ))}
                  </ScrollRow>
                </div>

              </div>
    
              {/* Tables */}
              <div
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',              // ✅ prevent horizontal scroll
                  WebkitOverflowScrolling: 'touch',
                  padding: isMobile ? '0 8px 6px 8px' : 0
                }}
              >
                <TableSelector
                  reservationTime={walkInTime}
                  selectedAssignedTables={selectedTables}
                  onTablesSelected={handleTablesSelected}
                  people={numPeople}
                  areaFilter={areaFilterFn}
                  tableFilter={null}
                />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    )}

    {/* Sitting Modal */}
    {sittingModal && (
      <Portal>
        <div className="modal-overlay" onClick={() => setSittingModal(false)} style={overlayMobileStyle}>
          <div className={`modal-sitting ${isMobile ? '' : 'centered'}`}
            onClick={(e) => e.stopPropagation()}
            style={sittingBoxStyle || undefined}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',   // ✅ critical
                minHeight: 0,     // ✅ critical for overflow child in flex
                overflow: 'hidden'
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: isMobile ? 8 : 20 }}>
                  <b>{tr('Sitting:')}</b>
                  <input
                    type="text"
                    placeholder={tr('Search reservations...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ flex: '1 1 180px', padding: 6, minWidth: 160 }}
                  />
                  <button
                    onClick={handleSitting}
                    disabled={
                      !selectedReservation ||
                      (!hasTables(selectedReservation) && !(assignTablesReservation?.id === selectedReservation?.id && assignTablesSelection.length > 0))
                    }
                    style={{ marginLeft: 'auto' }}
                  >{tr('Confirm')}</button>
                </div>
              </div>

              <div
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,                     // ✅ ensures flex scrolling works
                  overflowY: 'auto',                // ✅ enables vertical scrolling
                  overflowX: 'hidden',              // ✅ disables horizontal scroll
                  WebkitOverflowScrolling: 'touch', // ✅ smooth scroll on iOS/iPad
                  padding: isMobile ? '0 10px 12px 10px' : '0 0 12px 0',
                }}
              >
                {filteredSittingReservations?.length ? (
                  isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {filteredSittingReservations.map((res) => {
                        const selected = selectedReservation?.id === res.id;
                        return (
                          <div
                            key={res.id}
                            onClick={() => setSelectedReservation(res)}
                            style={{
                              border: '1px solid rgba(0,0,0,0.12)',
                              borderRadius: 14,
                              padding: '8px 8px',
                              background: selected ? 'rgba(0,90,180,0.06)' : 'white',
                              outline: selected ? '2px solid rgba(0,90,180,0.35)' : 'none',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              cursor: 'pointer',
                            }}
                          >
                            <>
                              {/* Row 1: id/time + tables button */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ minWidth: 72 }}>
                                  <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.05 }}>#{res.id}</div>
                                  <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.05 }}>{formatTimeGR(res.arrival)}</div>
                                </div>
                            
                                <button
                                  title={tr('Select table(s)')}
                                  onClick={(e) => { e.stopPropagation(); openAssignTables(res); }}
                                  style={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    maxWidth: '60vw',
                                    padding: '6px 8px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {hasTables(res) ? res.tables : tr('Select Tables')}
                                </button>
                              </div>
                            
                              {/* Row 2: name/phone left, people right */}
                              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                {/* Name (no label on mobile) */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    {res.client_phone ? (
                                      <a
                                        href={`tel:${res.client_phone}`}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          display: 'block',
                                          fontSize: 14,
                                          fontWeight: 700,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          lineHeight: 1.15,
                                          textDecoration: 'none',
                                          color: 'inherit',
                                        }}
                                        title={res.client_phone}
                                      >
                                        {res.client_lastname} {res.client_firstname}
                                      </a>
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 700,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          lineHeight: 1.15,
                                        }}
                                      >
                                        {res.client_lastname} {res.client_firstname}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              
                                {/* People (icon instead of label) */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span aria-label={tr('People')} title={tr('People')} style={{ fontSize: 16, opacity: 0.9 }}>
                                    {'\u{1F464}'}
                                  </span>
                                  <input
                                    type="number"
                                    min="1"
                                    max="999"
                                    value={res.people ?? 2}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => { e.stopPropagation(); updatePeopleInline(res, e.target.value); }}
                                    style={{ width: 64, textAlign: 'right', padding: '4px 6px' }}
                                  />
                                </div>
                              </div>
                            
                              {/* Optional comment, compact */}
                              {!!res.comment && (
                                <div style={{ marginTop: 6 }}>
                                  <div style={{ fontSize: 12, fontStyle: 'italic', wordBreak: 'break-word', lineHeight: 1.15 }}>
                                    {res.comment}
                                  </div>
                                </div>
                              )}
                            </>

                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      {/* keep your desktop table exactly as before */}
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{tr('Arrival')}</th>
                          <th>{tr('People')}</th>
                          <th>{tr('Tables')}</th>
                          <th>{tr('Name')}</th>
                          <th>{tr('Phone')}</th>
                          <th>{tr('Comment')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSittingReservations.map((res) => (
                          <tr
                            key={res.id}
                            onClick={() => setSelectedReservation(res)}
                            style={{
                              color: selectedReservation?.id === res.id ? 'darkBlue' : 'black',
                              fontWeight: selectedReservation?.id === res.id ? 'bold' : 'normal',
                            }}
                            className="service-row"
                          >
                            <td><b>{res.id}</b></td>
                            <td><i>{formatTimeGR(res.arrival)}</i></td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                max="999"
                                value={res.people ?? 2}
                                onChange={(e) => { e.stopPropagation(); updatePeopleInline(res, e.target.value); }}
                                style={{ width: 30, textAlign: 'right' }}
                              />
                            </td>
                            <td>
                              <button title={tr('Select table(s)')} onClick={(e) => { e.stopPropagation(); openAssignTables(res); }}>
                                {hasTables(res) ? res.tables : tr('Select Tables')}
                              </button>
                            </td>
                            <td>{res.client_lastname} {res.client_firstname}</td>
                            <td>{res.client_phone}</td>
                            <td><i>{res.comment}</i></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : (
                  <h4 style={{ margin: 12 }}>{tr('No more open reservations for today yet..')}</h4>
                )}
              </div>
            </div>
          </div>
        </div>
      </Portal>
    )}

    {/* Assign Tables (from Sitting) */}
    {assignTablesModal && assignTablesReservation && (
      <Portal>
        <div className="modal-overlay" onClick={() => setAssignTablesModal(false)} style={overlayMobileStyle}>
          <div className={`modal ${isMobile ? '' : 'centered'}`} onClick={(e) => e.stopPropagation()} style={assignTablesBoxStyle || undefined}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',     // ✅ desktop/tablet too
                minHeight: 0,       // ✅ allows the scroll child to shrink
                overflowX: 'hidden' // ✅ avoid horizontal scroll
              }}
            >
              <div style={stickyHeaderStyle || undefined}>
                <h3 style={{ margin: isMobile ? 8 : undefined, marginBottom: 8 }}>
                  {tr('Assign tables for reservation #')}{assignTablesReservation.id} - {assignTablesReservation.client_lastname} {assignTablesReservation.client_firstname}
                  <button
                    style={{ float: 'right' }}
                    onClick={async () => {
                      if (!assignTablesSelection.length) return;
                      try {
                        await axios.post(`${config.API_BASE_URL}/assignedtables`, {
                          reservationId: assignTablesReservation.id,
                          tables: assignTablesSelection,
                        });
                        await axios.post(`${config.API_BASE_URL}/assignedstates/${assignTablesReservation.id}`, { state: 5 });
                        setAssignTablesModal(false);
                        setSittingModal(false);
                        setAssignTablesReservation(null);
                        setAssignTablesSelection([]);
                        setSelectedReservation(null);
                        onRefresh();
                      } catch (err) {
                        console.error('Error assigning tables from Sitting:', err);
                      }
                    }}
                    disabled={!assignTablesSelection.length}
                  >
                    {tr('Confirm')}
                  </button>
                </h3>

                <div className="ts-area-filters" style={{ margin: isMobile ? '0 8px 8px 8px' : '6px 0 10px 0' }}>
                  <h4 className="ts-area-filters-title" style={{ margin: '3px 0' }}>{tr('Area Filters:')}</h4>
                
                  <ScrollRow
                    className="ts-area-filters-items"
                    isMobile={isMobile}
                    resetKey={assignTablesModal ? 1 : 0}
                    desktopStyle={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}
                  >
                    {visibleFilters.map(([key, label]) => (
                      <label key={key} style={{ userSelect: 'none', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!areaFilters[key]}
                          onChange={(e) => setAreaFilters(prev => ({ ...prev, [key]: e.target.checked }))}
                          style={{ marginRight: 6, ...(isMobile ? {} : { position: 'relative', top: 5 }) }}
                        />
                        {tr(label)}
                      </label>
                    ))}
                  </ScrollRow>
                </div>
                
              </div>

              <div
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,                     // ✅ keep this (critical)
                  overflowY: 'auto',                // ✅ vertical scroll
                  overflowX: 'hidden',              // ✅ no horizontal scroll
                  WebkitOverflowScrolling: 'touch', // ✅ smoother on iPad
                  padding: isMobile ? '0 8px 10px 8px' : '0 0 10px 0', // ✅ little bottom breathing room
                }}
              >
                <TableSelector
                  service_view={true}
                  reservationTime={assignTablesReservation.arrival}
                  selectedAssignedTables={assignTablesSelection}
                  activeReservationId={assignTablesReservation.id}
                  onTablesSelected={onAssignTablesSelected}
                  people={assignTablesReservation.people || 0}
                  areaFilter={(area) => areaMatchesFilters(area)}
                  tableFilter={null}
                />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    )}
  </>
);
}


export default memo(WalkedInSitting);
